import { BookOpen, Flag, Settings, Users } from 'lucide-react';
import type { Tenant } from './api-types';
import { canAccess } from './permissions';

export type Workspace = 'client' | 'expert';

export const EXPERT_PREFIX = '/expert-agents';
export const CLIENT_HOME = '/';

export const EXPERT_ROUTES = {
  experts: `${EXPERT_PREFIX}/experts`,
  flaggedAnswers: `${EXPERT_PREFIX}/flagged-answers`,
  training: `${EXPERT_PREFIX}/training`,
  // Reached from Settings only (not in the sidebar); Settings stays highlighted there.
  homeScreen: `${EXPERT_PREFIX}/home-screen`,
  settings: `${EXPERT_PREFIX}/settings`,
} as const;

export const EXPERT_HOME = EXPERT_ROUTES.experts;

export const EXPERT_NAV = [
  { name: 'Users', href: EXPERT_ROUTES.experts, icon: Users },
  { name: 'Flagged answers', href: EXPERT_ROUTES.flaggedAnswers, icon: Flag },
  { name: 'Training', href: EXPERT_ROUTES.training, icon: BookOpen },
  { name: 'Settings', href: EXPERT_ROUTES.settings, icon: Settings },
];

export const WORKSPACE_LABELS: Record<Workspace, string> = {
  client: 'Client Admin',
  expert: 'Expert Agents',
};

export function getWorkspaceFromPath(pathname: string): Workspace {
  return pathname === EXPERT_PREFIX || pathname.startsWith(`${EXPERT_PREFIX}/`) ? 'expert' : 'client';
}

export function isClientAdminEnabled(tenant: Tenant | null | undefined): boolean {
  return tenant?.client_admin_enabled !== false;
}

export function isDomainExpertEnabled(tenant: Tenant | null | undefined): boolean {
  return tenant?.domain_expert_enabled === true;
}

export function getAvailableWorkspaces(tenant: Tenant | null | undefined, role: string | undefined): Workspace[] {
  const workspaces: Workspace[] = [];
  if (isClientAdminEnabled(tenant)) workspaces.push('client');
  if (isDomainExpertEnabled(tenant) && EXPERT_NAV.some((item) => canAccess(role, item.href))) {
    workspaces.push('expert');
  }
  return workspaces;
}

export function getWorkspaceHome(workspace: Workspace, role: string | undefined): string {
  if (workspace === 'client') return CLIENT_HOME;
  return EXPERT_NAV.find((item) => canAccess(role, item.href))?.href ?? EXPERT_HOME;
}

const routeKey = (tenantId: string, workspace: Workspace) => `lawma_ws_route:${tenantId}:${workspace}`;
const lastWorkspaceKey = (tenantId: string) => `lawma_ws_last:${tenantId}`;

export function rememberWorkspaceRoute(tenantId: string | undefined, path: string): void {
  if (!tenantId) return;
  const workspace = getWorkspaceFromPath(path.split('?')[0]);
  try {
    localStorage.setItem(routeKey(tenantId, workspace), path);
    localStorage.setItem(lastWorkspaceKey(tenantId), workspace);
  } catch {
    /* storage unavailable */
  }
}

export function getRememberedWorkspaceRoute(tenantId: string | undefined, workspace: Workspace): string | null {
  if (!tenantId) return null;
  try {
    const path = localStorage.getItem(routeKey(tenantId, workspace));
    if (path && path.startsWith('/') && getWorkspaceFromPath(path.split('?')[0]) === workspace) return path;
  } catch {
    /* storage unavailable */
  }
  return null;
}

export function getLastWorkspace(tenantId: string | undefined): Workspace | null {
  if (!tenantId) return null;
  try {
    const value = localStorage.getItem(lastWorkspaceKey(tenantId));
    return value === 'client' || value === 'expert' ? value : null;
  } catch {
    return null;
  }
}

export function getLandingPath(tenant: Tenant | null | undefined, role: string | undefined): string {
  const available = getAvailableWorkspaces(tenant, role);
  if (available.length === 0) return CLIENT_HOME;
  const last = available.length > 1 ? getLastWorkspace(tenant?.id) : null;
  const workspace = last && available.includes(last) ? last : available[0];
  return getWorkspaceHome(workspace, role);
}
